import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Use the current origin for API calls to ensure same-origin requests
// when accessed via custom domains (e.g., kayaworld.ai vs *.emergent.host)
const API_BASE = `${window.location.origin}/api`;

// Create axios instance — cookies handle auth automatically
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 20000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Handle auth errors — attempt silent refresh before logging out
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve();
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/refresh') &&
      !originalRequest.url?.includes('/auth/login') &&
      !originalRequest.url?.includes('/auth/google-callback') &&
      !originalRequest.url?.includes('/auth/register')
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => api(originalRequest));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Cookie is sent automatically — no need to pass refresh_token in body
        await axios.post(`${API_BASE}/auth/refresh`, {}, { withCredentials: true });
        processQueue(null);
        return api(originalRequest);
      } catch (refreshErr) {
        processQueue(refreshErr);
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (email, password, name, terms_accepted = false) => api.post('/auth/register', { email, password, name, terms_accepted }),
  googleCallback: (sessionId) => api.post('/auth/google-callback', { session_id: sessionId }),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout', {}),
};

// Accounts API
export const accountsAPI = {
  getAll: (params) => api.get('/accounts', { params }),
  getById: (id) => api.get(`/accounts/${id}`),
  create: (data) => api.post('/accounts', data),
  update: (id, data) => api.put(`/accounts/${id}`, data),
  updateDescription: (id, description) => api.patch(`/accounts/${id}/description`, { description }),
  delete: (id) => api.delete(`/accounts/${id}`),
  seedDefaults: () => api.post('/seed-accounts'),
  seed: () => api.post('/seed-accounts'),
};

// Transactions API
export const transactionsAPI = {
  getAll: (params) => api.get('/transactions', { params }),
  getCounts: () => api.get('/transactions/counts'),
  getById: (id) => api.get(`/transactions/${id}`),
  update: (id, data) => api.put(`/transactions/${id}`, data),
  approve: (id) => api.post(`/transactions/${id}/approve`),
  reject: (id) => api.post(`/transactions/${id}/reject`),
  delete: (id) => api.delete(`/transactions/${id}`),
  postAll: () => api.post('/transactions/post-all'),
  approveAll: () => api.post('/transactions/approve-all'),
  reverse: (id) => api.post(`/transactions/${id}/reverse`),
  unapprove: (id) => api.post(`/transactions/${id}/unapprove`),
};

// Admin API
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  clearGL: () => api.delete('/admin/clear-gl'),
  clearAllTransactions: () => api.delete('/admin/clear-all-transactions'),
};

// Journal Entries API
export const journalAPI = {
  getAll: (params) => api.get('/journal-entries', { params }),
  getById: (id) => api.get(`/journal-entries/${id}`),
  create: (data) => api.post('/journal-entries', data),
};

// Customers API
export const customersAPI = {
  getAll: (params) => api.get('/customers', { params }),
  getById: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
};

// Invoices API
export const invoicesAPI = {
  getAll: (params) => api.get('/invoices', { params }),
  getById: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  post: (id) => api.post(`/invoices/${id}/post`),
  markPaid: (id) => api.post(`/invoices/${id}/mark-paid`),
  cancel: (id) => api.post(`/invoices/${id}/cancel`),
  send: (id) => api.post(`/invoices/${id}/send`),
  sendReminder: (id, level) => api.post(`/invoices/${id}/send-reminder`, { reminder_level: level }),
  getPdf: (id) => `${API_BASE}/invoices/${id}/pdf`,
  getBillingConfig: () => api.get('/billing-config'),
  updateBillingConfig: (data) => api.put('/billing-config', data),
};


// Upload API
export const uploadAPI = {
  uploadAndProcess: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload-and-process-file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getUploadStatus: (fileId) => api.get(`/upload-status/${fileId}`),
  process: (fileId) => api.post(`/upload/${fileId}/process`),
  getDocuments: () => api.get('/source-documents'),
  getDocumentSummary: (fileId) => api.get(`/source-documents/${fileId}/summary`),
  deleteDocument: (fileId) => api.delete(`/source-documents/${fileId}`),
};

// Reports API
export const reportsAPI = {
  getTrialBalance: (params) => api.get('/reports/trial-balance', { params }),
  getProfitLoss: (params) => api.get('/reports/profit-loss', { params }),
  getBalanceSheet: (params) => api.get('/reports/balance-sheet', { params }),
  getGeneralLedger: (params) => api.get('/reports/general-ledger', { params }),
  getCashFlow: (params) => api.get('/reports/cash-flow', { params }),
  getVAT: (params) => api.get('/reports/vat', { params }),
  getExpenseAnalysis: (params) => api.get('/reports/expense-analysis', { params }),
  getFinancialHealth: () => api.get('/reports/financial-health'),
  getHeartbeat: () => api.get('/reports/heartbeat'),
  getIntegrityCheck: () => api.get('/reports/integrity-check'),
  exportCSV: (reportType, params) => {
    const queryStr = new URLSearchParams({ report_type: reportType, ...params }).toString();
    return `${API_BASE}/reports/export/csv?${queryStr}`;
  },
  exportPDF: (reportType, params) => {
    const queryStr = new URLSearchParams({ report_type: reportType, ...params }).toString();
    return `${API_BASE}/reports/export/pdf?${queryStr}`;
  },
  exportRaw: (endpoint, params, responseType = 'text') => {
    return api.get(endpoint, { params, responseType, timeout: 30000 });
  },
};

// Dashboard API
export const dashboardAPI = {
  get: () => api.get('/dashboard'),
};

// Allowed Emails API
export const allowedEmailsAPI = {
  getAll: () => api.get('/auth/allowed-emails'),
  add: (email) => api.post('/auth/allowed-emails', { email }),
  delete: (id) => api.delete(`/auth/allowed-emails/${id}`),
};

// Tax Config API
export const taxConfigAPI = {
  getActive: () => api.get('/tax-config'),
  getAll: () => api.get('/tax-configs'),
  getPresets: () => api.get('/tax-config/presets'),
  activate: (data) => api.post('/tax-config', data),
  update: (id, data) => api.put(`/tax-config/${id}`, data),
  deactivate: (id) => api.delete(`/tax-config/${id}`),
};

// Organization API
export const organizationAPI = {
  get: () => api.get('/organization'),
  setup: (data) => api.post('/organization/setup', data),
  update: (data) => api.put('/organization', data),
  getCountries: () => api.get('/organization/countries'),
  changeCountry: (country_code) => api.post('/organization/change-country', { country_code }),
  lockFiscalYear: (year) => api.post('/organization/lock-fiscal-year', { year }),
  unlockFiscalYear: (year) => api.post('/organization/unlock-fiscal-year', { year }),
  previewClosingEntries: (year) => api.post('/organization/preview-closing-entries', { year }),
  closeFiscalYear: (year) => api.post('/organization/close-fiscal-year', { year }),
  getAutoApprovalEligibility: () => api.get('/organization/auto-approval-eligibility'),
  getEntities: () => api.get('/organization/entities'),
  createEntity: (data) => api.post('/organization/entities', data),
  switchEntity: (orgId) => api.post('/organization/switch-entity', { org_id: orgId }),
};

// Credits API
export const creditsAPI = {
  get: () => api.get('/credits'),
  getHistory: (limit = 50) => api.get('/credits/history', { params: { limit } }),
  topup: (credits) => api.post('/credits/topup', { credits, origin_url: window.location.origin }),
};

// Checkout API (Stripe — credit top-ups)
export const checkoutAPI = {
  status: (sessionId) => api.get(`/checkout/status/${sessionId}`),
};

// Historical Data Import API
export const historicalDataAPI = {
  upload: (file, fiscalYear, importType = 'trial_balance') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('fiscal_year', fiscalYear);
    formData.append('import_type', importType);
    return api.post('/historical-data/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  list: () => api.get('/historical-data/imports'),
  get: (importId) => api.get(`/historical-data/${importId}`),
  mapAccounts: (importId) => api.post(`/historical-data/${importId}/map-accounts`),
  updateMappings: (importId, mappings) => api.put(`/historical-data/${importId}/mappings`, mappings),
  validate: (importId) => api.get(`/historical-data/${importId}/validate`),
  confirm: (importId) => api.post(`/historical-data/${importId}/confirm`),
  delete: (importId) => api.delete(`/historical-data/${importId}`),
};

// Team API
export const teamAPI = {
  listMembers: () => api.get('/team/members'),
  invite: (email, role = 'accountant') => api.post('/team/invite', { email, role }),
  listInvitations: () => api.get('/team/invitations'),
  revokeInvitation: (inviteId) => api.delete(`/team/invitations/${inviteId}`),
  updateRole: (userId, role) => api.put(`/team/members/${userId}/role`, { role }),
  removeMember: (userId) => api.delete(`/team/members/${userId}`),
};

// Platform Admin API
export const platformAdminAPI = {
  getDashboard: () => api.get('/platform-admin/dashboard'),
  getOrganizations: (params) => api.get('/platform-admin/organizations', { params }),
  getUsers: (params) => api.get('/platform-admin/users', { params }),
  adjustCredits: (orgId, amount, reason) => api.put(`/platform-admin/organizations/${orgId}/credits`, { amount, reason }),
  changeTier: (orgId, tier) => api.put(`/platform-admin/organizations/${orgId}/tier`, { tier }),
  suspendUser: (userId, suspended) => api.put(`/platform-admin/users/${userId}/suspend`, { suspended }),
  togglePlatformAdmin: (userId, grant) => api.put(`/platform-admin/users/${userId}/platform-admin`, { grant }),
  deleteUser: (userId) => api.delete(`/platform-admin/users/${userId}`),
  cancelSubscription: (orgId) => api.post(`/platform-admin/organizations/${orgId}/cancel-subscription`),
  // Analytics
  getAnalyticsOverview: (days = 30) => api.get(`/platform-admin/analytics/overview?days=${days}`),
  getAnalyticsEfficiency: (days = 30) => api.get(`/platform-admin/analytics/efficiency?days=${days}`),
  getAnalyticsUserDetail: (userId, days = 30) => api.get(`/platform-admin/analytics/users/${userId}?days=${days}`),
  getAnalyticsNps: (days = 30) => api.get(`/platform-admin/analytics/nps?days=${days}`),
  // Coupons
  getCoupons: (params) => api.get('/platform-admin/coupons', { params }),
  createCoupon: (data) => api.post('/platform-admin/coupons', data),
  toggleCoupon: (couponId) => api.put(`/platform-admin/coupons/${couponId}/toggle`),
  deleteCoupon: (couponId) => api.delete(`/platform-admin/coupons/${couponId}`),
  // Data Management
  getDataStats: () => api.get('/platform-admin/data/stats'),
  purgeOrganizations: (keepOrgId) => api.delete(`/platform-admin/data/organizations?keep_org_id=${keepOrgId}`),
  purgeTransactions: (orgId) => api.delete(`/platform-admin/data/transactions${orgId ? `?org_id=${orgId}` : ''}`),
  purgeAnalytics: () => api.delete('/platform-admin/data/analytics'),
  fullPlatformReset: (keepOrgId) => api.delete(`/platform-admin/data/full-reset?keep_org_id=${keepOrgId}`),
  // Admin Management
  listAdmins: () => api.get('/platform-admin/admins'),
  grantAdminByEmail: (email) => api.post('/platform-admin/admins/grant-by-email', { email }),
  revokeAdmin: (userId) => api.delete(`/platform-admin/admins/${userId}`),
};

export const analyticsAPI = {
  track: (feature, action, duration_ms, metadata) => api.post('/analytics/track', { feature, action, duration_ms, metadata }),
  submitNps: (rating, comment) => api.post('/analytics/nps', { rating, comment }),
  shouldPromptNps: () => api.get('/analytics/nps/should-prompt'),
};

// Public coupon redemption
export const couponAPI = {
  redeem: (code) => api.post('/coupons/redeem', { code }),
};

// Billing API
export const billingAPI = {
  getSubscription: () => api.get('/billing/subscription'),
  subscribe: (tier) => api.post('/billing/subscribe', { tier, origin_url: window.location.origin }),
  upgrade: (tier) => api.post('/billing/upgrade', { tier }),
  downgrade: (tier) => api.post('/billing/downgrade', { tier }),
  cancel: () => api.post('/billing/cancel'),
  reactivate: () => api.post('/billing/reactivate'),
  getPortalUrl: () => api.get('/billing/portal'),
  getInvoices: () => api.get('/billing/invoices'),
};

export const aiAPI = {
  getSuggestions: (contextType, payload) => api.post('/ai/suggestions', { context_type: contextType, payload }),
  acceptSuggestion: (contextType, descriptionPattern, acceptedText, acceptedData) =>
    api.post('/ai/accept-suggestion', { context_type: contextType, description_pattern: descriptionPattern, accepted_text: acceptedText, accepted_data: acceptedData }),
};

export const notificationsAPI = {
  list: (unreadOnly = false) => api.get(`/notifications${unreadOnly ? '?unread_only=true' : ''}`),
  markRead: (id) => api.post(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
  checkOverdue: () => api.post('/notifications/check-overdue'),
};

export const pushAPI = {
  getVapidKey: () => api.get('/push/vapid-key'),
  subscribe: (subscription) => api.post('/push/subscribe', subscription),
  unsubscribe: (subscription) => api.post('/push/unsubscribe', subscription),
  test: () => api.post('/push/test'),
};


export default api;
